/**
 * Card genérica para gráficas y resúmenes. Trae:
 *   - Header con título + subtítulo
 *   - Botones hover-revealed para descargar como PNG (html-to-image)
 *     y para abrir el contenido en un modal grande
 *
 * Compartido entre Resumen y Análisis para que cualquier card de finanzas
 * se pueda exportar/expandir sin duplicar lógica.
 */
import { useRef, useState } from "react";
import { Download, Maximize2 } from "lucide-react";
import clsx from "clsx";

export function ChartCard({
  title,
  subtitle,
  children,
  className,
  actions,
  pdfKey,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
  /** Acciones extra a la derecha del subtítulo, antes de los iconos. */
  actions?: React.ReactNode;
  /** Si se setea, el outer div lleva `data-pdf-chart="<key>"` para que
   *  el exporter de PDF lo encuentre y capture. */
  pdfKey?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    if (!ref.current || busy) return;
    setBusy(true);
    try {
      const { toPng } = await import("html-to-image");
      const dataUrl = await toPng(ref.current, {
        pixelRatio: 2,
        backgroundColor: "#ffffff",
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png`;
      a.click();
    } catch (e) {
      console.error("download chart failed", e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div
        ref={ref}
        className={clsx("card-elevated p-5 space-y-3 group relative", className)}
        data-pdf-chart={pdfKey}
      >
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70">
            {title}
          </h3>
          <div className="flex items-center gap-1">
            {subtitle && <span className="text-xs text-base-content/55">{subtitle}</span>}
            {actions}
            <div className="flex items-center gap-0.5 ml-1">
              <button
                type="button"
                onClick={download}
                disabled={busy}
                className="btn btn-ghost btn-xs btn-square text-base-content/55 hover:text-primary"
                title="Descargar como imagen"
                aria-label="Descargar"
              >
                <Download className="size-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="btn btn-ghost btn-xs btn-square text-base-content/55 hover:text-primary"
                title="Ver más grande"
                aria-label="Expandir"
              >
                <Maximize2 className="size-3.5" />
              </button>
            </div>
          </div>
        </div>
        {children}
      </div>

      {expanded && (
        <div
          className="modal modal-open"
          onClick={(e) => {
            if (e.target === e.currentTarget) setExpanded(false);
          }}
        >
          <div className="modal-box max-w-6xl w-full">
            <div className="flex items-baseline justify-between gap-2 flex-wrap mb-4">
              <h3 className="text-lg font-display font-semibold">{title}</h3>
              <div className="flex items-center gap-2">
                {subtitle && <span className="text-xs text-base-content/55">{subtitle}</span>}
                <button
                  type="button"
                  onClick={download}
                  disabled={busy}
                  className="btn btn-ghost btn-sm"
                >
                  <Download className="size-4" /> Descargar
                </button>
                <button
                  type="button"
                  onClick={() => setExpanded(false)}
                  className="btn btn-ghost btn-sm"
                >
                  Cerrar
                </button>
              </div>
            </div>
            <div className="space-y-3">{children}</div>
          </div>
        </div>
      )}
    </>
  );
}
